/**
 * The living forms on the soul cards: Ben's renderer (bailey-soul-handoff,
 * 29 Sept 2026), kept as designed. Dependency-free WebGL 1, one canvas per
 * card, a 64 by 96 sphere pushed about by the vertex shader, capped near
 * 30fps and pixel density 2, idle when offscreen or the tab is hidden.
 *
 * Changes from the handoff: souls are keyed by data-soul rather than card
 * order, a lost context drops back to the CSS form instead of a blank, and
 * the renderer skips cards it has already dressed.
 *
 * The card's `aria-pressed` drives the chosen form (bigger, faster, deeper
 * folds); `data-motion="false"` on the root holds every form still.
 */

const VERTEX = `
 precision highp float;
 attribute vec3 aPosition;
 uniform float uTime,uKind,uSelected,uBurst,uAspect;
 varying vec3 vNormal,vPosition;
 float wave(vec3 p){return sin(p.x)*cos(p.y)*sin(p.z);}
 vec3 form(vec3 n){
  float t=uTime;
  vec3 q=n;
  float broad=wave(q*2.6+vec3(t*.34,t*.27,-t*.31));
  float folds=wave(q*5.1+vec3(-t*.26,t*.32,t*.2));
  float fine=wave(q*8.5+vec3(t*.16,-t*.19,t*.14));
  float r=1.;
  if(uKind<.5){r+=.19*broad+.065*folds+.016*fine+.065*sin(q.y*3.7+q.x*2.+t*.43);q*=vec3(1.07,.94,1.);}
  else if(uKind<1.5){r+=.24*broad+.095*folds+.018*fine+.06*sin(q.x*4.+q.z*3.-t*.6);q*=vec3(1.,1.04,.99);}
  else if(uKind<2.5){r+=.13*broad+.047*folds+.011*fine;q*=vec3(.94,1.07,1.);}
  else{r+=.17*broad+.055*folds+.015*fine+.045*sin(q.y*5.+t*.55);q*=vec3(1.04,.96,1.);}
  r=1.+(r-1.)*(1.+uSelected*.6);
  r+=sin(t*1.05)*(.025+uSelected*.035)+uBurst*.13*sin(q.y*6.-uTime*3.);
  return q*r;
 }
 mat3 turn(float a){float c=cos(a),s=sin(a);return mat3(c,0.,-s,0.,1.,0.,s,0.,c);}
 void main(){
  vec3 n=normalize(aPosition);
  vec3 tangent=normalize(cross(n,abs(n.y)<.9?vec3(0.,1.,0.):vec3(1.,0.,0.)));
  vec3 bitangent=normalize(cross(n,tangent));
  vec3 p=form(n);
  vec3 du=form(normalize(n+tangent*.006))-p;
  vec3 dv=form(normalize(n+bitangent*.006))-p;
  mat3 rotation=turn(uTime*.13+uKind*1.7);
  vec3 norm=rotation*normalize(cross(du,dv));
  p=rotation*p;
  p*=.72+uSelected*.34;
  vPosition=p;vNormal=norm;
  gl_Position=vec4(p.x*.69/uAspect,p.y*.69-.015,-p.z*.22,1.);
 }`;

const FRAGMENT = `
 precision highp float;
 varying vec3 vNormal,vPosition;
 uniform vec3 uColor;
 uniform float uKind,uSelected;
 float softbox(vec3 reflection,vec3 dir,float sharpness){return pow(max(dot(reflection,normalize(dir)),0.),sharpness);}
 void main(){
  vec3 n=normalize(vNormal);
  vec3 view=normalize(vec3(0.,0.,5.)-vPosition);
  vec3 reflection=reflect(-view,n);
  vec3 key=normalize(vec3(-2.5,3.,4.));
  float diffuse=max(dot(n,key),0.);
  float fill=max(dot(n,normalize(vec3(3.,.6,1.))),0.);
  float rim=pow(1.-max(dot(n,view),0.),2.6);
  vec3 base=uColor;
  float pearl=uKind>1.5&&uKind<2.5?.38:.12;
  base=mix(base,vec3(.9,.91,1.),pearl*(.5+.5*sin(n.x*3.+n.y*2.)));
  vec3 col=base*(.36+.68*diffuse+.12*fill);
  col+=vec3(.42,.23,.6)*rim*.16;
  col+=base*pow(max(dot(-n,key),0.),2.)*.18;
  float shine=softbox(reflection,vec3(-.55,.8,1.),28.);
  float broad=softbox(reflection,vec3(-.8,1.,1.4),5.);
  float strip=softbox(reflection,vec3(1.,.35,.7),48.);
  float bottom=softbox(reflection,vec3(.2,-1.,.8),22.);
  col+=vec3(1.,.94,.88)*(shine*.54+broad*.2);
  col+=vec3(.83,.91,1.)*(strip*.66+bottom*.2);
  col=mix(col,vec3(.8,.9,1.),rim*(uKind>1.5&&uKind<2.5?.3:.13));
  col=pow(max(col,vec3(0.)),vec3(.88));
  gl_FragColor=vec4(col,1.);
 }`;

// The form's own material colour and idle tempo, per soul (the guide's table).
const KINDS = {
    warm: { kind: 0, colour: [0.98, 0.30, 0.27], tempo: 0.72 },
    fun: { kind: 1, colour: [1.0, 0.69, 0.14], tempo: 1.05 },
    polished: { kind: 2, colour: [0.39, 0.62, 1.0], tempo: 0.43 },
    quick: { kind: 3, colour: [0.23, 0.76, 0.49], tempo: 0.82 }
};

let sphere = null;
function geometry() {
    if (sphere) return sphere;
    const points = []; const indices = []; const rows = 64; const cols = 96;
    for (let y = 0; y <= rows; y++) {
        const v = (y / rows) * Math.PI;
        for (let x = 0; x <= cols; x++) {
            const u = (x / cols) * Math.PI * 2;
            points.push(Math.sin(v) * Math.cos(u), Math.cos(v), Math.sin(v) * Math.sin(u));
        }
    }
    for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) {
        const a = y * (cols + 1) + x; const b = a + cols + 1;
        indices.push(a, b, a + 1, b, b + 1, a + 1);
    }
    sphere = { points: new Float32Array(points), indices: new Uint16Array(indices) };
    return sphere;
}

function shader(gl, type, source) {
    const s = gl.createShader(type);
    gl.shaderSource(s, source);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
}

export const webglAvailable = () => {
    try {
        const c = document.createElement('canvas');
        return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) { return false; }
};

/**
 * Dress every [data-soul] button under `root` with a living form. Returns
 * { destroy } to call on unmount and before any remount.
 */
export function mountSoulForms(root) {
    if (!root) throw new Error('A soul-card container is required');
    let stopped = false; let frameId = null;
    const handlers = [];
    const items = [];
    const motion = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
    const { points, indices } = geometry();

    const drop = (item) => {
        const at = items.indexOf(item);
        if (at !== -1) items.splice(at, 1);
        item.button.classList.remove('is-rendered');
        if (item.canvas.parentNode) item.canvas.remove();
    };

    root.querySelectorAll('[data-soul]').forEach((button) => {
        const spec = KINDS[button.getAttribute('data-soul')];
        if (!spec || button.classList.contains('is-rendered')) return;
        const canvas = document.createElement('canvas');
        canvas.className = 'soul-canvas';
        canvas.setAttribute('aria-hidden', 'true');
        button.appendChild(canvas);
        const gl = canvas.getContext('webgl', { alpha: true, antialias: true, premultipliedAlpha: false });
        if (!gl) { canvas.remove(); return; }
        try {
            const p = gl.createProgram();
            gl.attachShader(p, shader(gl, gl.VERTEX_SHADER, VERTEX));
            gl.attachShader(p, shader(gl, gl.FRAGMENT_SHADER, FRAGMENT));
            gl.linkProgram(p);
            if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
            gl.useProgram(p);
            const buffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
            gl.bufferData(gl.ARRAY_BUFFER, points, gl.STATIC_DRAW);
            const a = gl.getAttribLocation(p, 'aPosition');
            gl.enableVertexAttribArray(a);
            gl.vertexAttribPointer(a, 3, gl.FLOAT, false, 0, 0);
            const ib = gl.createBuffer();
            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
            gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
            gl.enable(gl.DEPTH_TEST);
            gl.clearColor(0, 0, 0, 0);
            const uniforms = {};
            ['uTime', 'uKind', 'uSelected', 'uBurst', 'uAspect', 'uColor'].forEach((k) => { uniforms[k] = gl.getUniformLocation(p, k); });
            const item = { canvas, button, gl, p, uniforms, spec, selected: 0, burst: 0, time: spec.kind * 3.1 };
            items.push(item);
            const onClick = () => { item.burst = 1; };
            button.addEventListener('click', onClick);
            handlers.push([button, 'click', onClick]);
            // A lost context (too many contexts, a backgrounded app) shows
            // the CSS form again rather than a blank card.
            const onLost = (e) => { e.preventDefault(); drop(item); };
            canvas.addEventListener('webglcontextlost', onLost);
            handlers.push([canvas, 'webglcontextlost', onLost]);
            button.classList.add('is-rendered');
        } catch (error) {
            canvas.remove();
            // eslint-disable-next-line no-console
            console.warn('Soul form renderer unavailable', error);
        }
    });

    let last = 0; let visible = true;
    let observer = null;
    if (typeof IntersectionObserver === 'function') {
        observer = new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; });
        observer.observe(root);
    }

    function frame(now) {
        if (stopped) return;
        if (!root.isConnected) { destroy(); return; }
        frameId = requestAnimationFrame(frame);
        if (now - last < 32) return;
        const dt = Math.min((now - last) / 1000, 0.05);
        last = now;
        if (!visible || document.hidden || !items.length) return;
        const animate = !motion.matches && root.getAttribute('data-motion') !== 'false';
        for (const o of items) {
            const { gl, uniforms: u, canvas, button, spec } = o;
            const rect = canvas.getBoundingClientRect();
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const w = Math.round(rect.width * dpr); const h = Math.round(rect.height * dpr);
            if (!w || !h) continue;
            if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; gl.viewport(0, 0, w, h); }
            const selected = button.getAttribute('aria-pressed') === 'true';
            o.selected += (Number(selected) - o.selected) * (animate ? 1 - Math.exp(-dt * 6) : 1);
            o.burst *= Math.exp(-dt * 2.1);
            if (animate) o.time += dt * spec.tempo * (1 + o.selected * 2.2);
            gl.useProgram(o.p);
            gl.uniform1f(u.uTime, o.time);
            gl.uniform1f(u.uKind, spec.kind);
            gl.uniform1f(u.uSelected, o.selected);
            gl.uniform1f(u.uBurst, animate ? o.burst : 0);
            gl.uniform1f(u.uAspect, w / h);
            gl.uniform3fv(u.uColor, spec.colour);
            gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
            gl.drawElements(gl.TRIANGLES, indices.length, gl.UNSIGNED_SHORT, 0);
        }
    }
    frameId = requestAnimationFrame(frame);

    function destroy() {
        if (stopped) return;
        stopped = true;
        if (frameId) cancelAnimationFrame(frameId);
        if (observer) observer.disconnect();
        handlers.forEach(([el, name, fn]) => el.removeEventListener(name, fn));
        items.slice().forEach((o) => {
            drop(o);
            try { const ext = o.gl.getExtension('WEBGL_lose_context'); if (ext) ext.loseContext(); } catch (e) { /* gone already */ }
        });
    }
    return { destroy, count: () => items.length };
}
